import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { FaTrashAlt } from "react-icons/fa";
import { defaultAction } from "@actions/defaultAction";
import { deleteCardInCart } from "@actions/buyOrders";
import {
  CART_CONFIRM_DELETE,
  CART_CLEAR_CONFIRM_DELETE,
  CLEAR_CART_BUY_CART_ERROR,
  CART_LOADING,
  UPDATE_CART_BUY_ERROR
} from "@constants/actionTypes";
import loadable from "@loadable/component";
const MessageContainer = loadable(() =>
  import(/* webpackChunkName: "MessageContainer" */ "./MessageContainer")
);
import {
  FirstCell,
  SecondCell,
  ThirdCell,
  FourthCell,
  FifthCell,
  SixthCell,
  SeventhCell,
  AdditionalLine
} from "@components/CartTableCell";
import { FaChevronDown } from "react-icons/fa";
import { currencyFormatter } from "@utils";
import DeleteCardTrashCan from "@components/DeleteCardTrashCan";
import { useDispatch, useSelector } from "react-redux";

const CardContainer = styled.div`
  @media (max-width: 700px) {
    position: relative;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 150px 1fr 125px 1fr 1fr 88px;
  grid-column-gap: 15px;
  position: relative;
  padding-top: 33px;
  padding-bottom: 15px;
  align-items: center;
  opacity: ${props => props.opacity};
  border-bottom: ${props => props.borderBottom};

  @media (max-width: 1650px) {
    grid-template-columns: 71px 150px 1fr 125px 1fr 1fr 88px;
  }
  @media (max-width: 1500px) {
    grid-template-columns: 67px 137px 83px 1fr 75px 99px 76px;
  }
  @media (max-width: 1390px) {
    grid-template-columns: 67px 128px 69px 96px 1fr 1fr 76px;
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 128px 1fr 1fr 1fr 1fr 88px;
  }
  @media (max-width: 1000px) {
    grid-template-columns: 67px 128px 1fr 97px 95px 1fr 88px;
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr 128px 1fr 97px 95px 85px 88px;
  }
  @media (max-width: 835px) {
    grid-template-columns: 56px 90px 1fr 88px 1fr 75px 77px;
  }
  @media (max-width: 700px) {
    display: none;
  }
`;

const MainMobileContainer = styled.div`
  @media (min-width: 701px) {
    display: none;
  }

  padding-top: 33px;
  padding-bottom: 25px;
  align-items: center;
  opacity: ${props => props.opacity};
  border-bottom: ${props => props.borderBottom};
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;

  @media (max-width: 355px) {
    grid-template-columns: auto auto;
  }
`;

const ModalContainer = styled.div`
  position: absolute;
  width: 100%;
  z-index: 9;
`;

const ConfirmMainContainer = styled(ModalContainer)`
  background-color: white;
`;

const IconSpan = styled.span`
  cursor: pointer;
  width: 30px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
`;

const ExpansionDiv = styled.div`
  justify-self: flex-end;
  width: 30px;
`;

const MobileCard = styled.div`
  display: ${props => props.setDisplay};
`;

const MobileCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 23px;
  align-items: center;
  margin-top: 25px;

  @media (max-width: 500px) {
    grid-template-columns: auto auto;
  }
`;

const LineItem = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #777984;
`;

const ErrorMainContainer = styled(ModalContainer)`
  @media (max-width: 700px) {
    bottom: 8px;
  }
`;

const TotalValue = styled.div`
  font-weight: bold;
  color: #1f2021;
  justify-self: flex-end;
`;

const ValueSpan = styled.span`
  color: #777984;
`;

const ForSpan = styled.span`
  color: #777984;
  font-weight: normal;
  margin-right: 7px;
  margin-left: 7px;
`;

const HeaderLeftGrid = styled.div`
  display: grid;
  grid-template-columns: 65px 1fr;
  align-items: center;
  grid-gap: 20px;

  @media (max-width: 430px) {
    grid-gap: 13px;
  }
  @media (max-width: 400px) {
    grid-gap: 9px;
  }
`;

const HeaderRightSide = styled.div`
  display: grid;
  grid-template-columns: 1fr 33px;
  align-items: center;
  font-size: 16px;

  @media (max-width: 430px) {
    grid-template-columns: 1fr 24px;
    font-size: 15px;
  }
  @media (max-width: 430px) {
    font-size: 14px;
  }
  @media (max-width: 385px) {
    font-size: 13px;
  }
`;

const MobileOnlyDiv = styled.div`
  @media (min-width: 356px) {
    display: none;
  }
`;

const MobileOnlyDivFlexEnd = styled(MobileOnlyDiv)`
  justify-self: flex-end;
`;

const LargeMobileOnly = styled.div`
  @media (max-width: 355px) {
    display: none;
  }
`;

const CardInBigBuyCart = ({
  index,
  card,
  card: {
    id,
    balance,
    type,
    merchant,
    quantity,
    percentOff,
    cost,
    to,
    message
  },
  slug,
  merchantArray
}) => {
  const dispatch = useDispatch();
  const cartLoading = useSelector(state => state.cartBuy.cartLoading);
  const cartId = useSelector(state => state.cartBuy.cartId);
  const cartOpenConfirmModal = useSelector(
    state => state.cartBuy.cartOpenConfirmModal
  );
  const cartError = useSelector(state => state.cartBuy.cartError);
  const cardArrayLength = useSelector(
    state => Object.values(state.cardsBuy).length
  );

  const [expanded, setExpanded] = useState(false);
  const [opacityLevel, changeOpacityLevel] = useState(1);
  useEffect(() => {
    if (cartLoading.status && cartLoading.cardId === id) {
      changeOpacityLevel(0.3);
    } else {
      changeOpacityLevel(1);
    }
  }, [cartLoading.status]);

  if (!balance) {
    return <div />;
  }

  const handleCancelCardClick = () => {
    confirmDelete({
      status: true,
      cartId: cartId,
      cardId: id
    });
  };

  const confirmDeleteClick = () => {
    dispatch(
      deleteCardInCart(cartOpenConfirmModal.cartId, cartOpenConfirmModal.cardId)
    );

    clearConfirmModal();

    const merchantName = merchantArray.name;
    ga &&
      ga(
        "send",
        "event",
        "buyMicroConversions",
        "removeFromCart",
        merchantName
      );
  };

  const cancelConfirmClick = () => {
    clearConfirmModal();
  };

  const handleErrorActionClick = () => {
    clearCartBuyErrorMessage();
  };

  const handleOpenClick = () => {
    setExpanded(!expanded);
  };

  const confirmDelete = status => {
    dispatch(defaultAction(CART_CONFIRM_DELETE, status));
  };

  const clearConfirmModal = () =>
    dispatch(defaultAction(CART_CLEAR_CONFIRM_DELETE));

  const clearCartBuyErrorMessage = () =>
    dispatch(defaultAction(CLEAR_CART_BUY_CART_ERROR));
  return (
    <CardContainer className="main-cart-grid">
      {cartOpenConfirmModal.status && cartOpenConfirmModal.cardId === id && (
        <ConfirmMainContainer>
          <MessageContainer
            iconElement={{
              element: <FaTrashAlt size={15} />,
              style: { opacity: 0.32 }
            }}
            message={`Are you sure you'd like to remove ${
              quantity > 1 ? "these cards" : "this card"
            }?`}
            action={[
              { text: "Yes", action: confirmDeleteClick },
              { text: "No", action: cancelConfirmClick }
            ]}
            classes={"popup-cart-confirm-message"}
          />
        </ConfirmMainContainer>
      )}

      {cartError && cartError.errMsg && cartError.cardId === id && (
        <ErrorMainContainer>
          <MessageContainer
            message={cartError.errMsg}
            action={[{ text: "Okay", action: handleErrorActionClick }]}
            classes={"popup-cart-error-message"}
            icon={"https://cdn.cardcash.com/website/ui/elements/error-icon.svg"}
          />
        </ErrorMainContainer>
      )}
      <MainGrid
        opacity={opacityLevel}
        borderBottom={
          cardArrayLength > index + 1
            ? "solid 1px rgba(119, 121, 132, 0.15)"
            : ""
        }
      >
        <FirstCell slug={slug} img={merchantArray[merchant].image} />
        <SecondCell name={merchantArray[merchant].name} />
        <ThirdCell
          card={card}
          clearTheMessage={clearCartBuyErrorMessage}
          confirmDelete={confirmDelete}
          clearConfirmModal={clearConfirmModal}
          cartError={cartError}
          cartOpenConfirmModal={cartOpenConfirmModal}
          cartLoading={cartLoading}
          loadingAction={CART_LOADING}
          updateCartError={UPDATE_CART_BUY_ERROR}
        />
        <FourthCell index={index} type={type} />
        <FifthCell balance={balance} />
        <SixthCell type={type} percentOff={percentOff} />
        <SeventhCell index={index} cost={cost} />

        <AdditionalLine
          type={type}
          to={to}
          message={message}
          handleCancelCardClick={handleCancelCardClick}
        />
      </MainGrid>

      <MainMobileContainer
        opacity={opacityLevel}
        borderBottom={
          cardArrayLength > index + 1
            ? "solid 1px rgba(119, 121, 132, 0.15)"
            : ""
        }
      >
        <HeaderGrid>
          <HeaderLeftGrid>
            <DeleteCardTrashCan
              slug={slug}
              setClass={"cart-page-delete-trash"}
              img={merchantArray[merchant].image}
              handleClick={handleCancelCardClick}
            />
            <LargeMobileOnly>
              <SixthCell
                type={type}
                percentOff={percentOff}
                classFromParent={"mobile-cart-page-percent"}
              />
            </LargeMobileOnly>
          </HeaderLeftGrid>
          <HeaderRightSide>
            <TotalValue>
              <ValueSpan>{currencyFormatter(balance)}</ValueSpan>
              <ForSpan>for</ForSpan>
              {currencyFormatter(cost)}
            </TotalValue>
            <ExpansionDiv>
              <IconSpan onClick={handleOpenClick}>
                <FaChevronDown
                  style={{
                    color: expanded ? "#1f2021" : "rgba(119, 121, 132, 0.5)",
                    fontSize: "14px",
                    transform: expanded ? "rotate(180deg)" : "inherit",
                    transition: "500ms transform"
                  }}
                />
              </IconSpan>
            </ExpansionDiv>
          </HeaderRightSide>
        </HeaderGrid>
        <MobileCard setDisplay={expanded ? "block" : "none"}>
          <MobileCardGrid>
            <LineItem>Name</LineItem>
            <SecondCell
              dontSplit={true}
              styles={{ justifySelf: "flex-end" }}
              name={merchantArray[merchant].name}
              classFromParent={"mobile-cart-page-name"}
            />
            <MobileOnlyDiv>
              <LineItem>% Off</LineItem>
            </MobileOnlyDiv>
            <MobileOnlyDivFlexEnd>
              <SixthCell
                type={type}
                percentOff={percentOff}
                classFromParent={"mobile-cart-page-percent"}
              />
            </MobileOnlyDivFlexEnd>
            <LineItem>Type</LineItem>
            <FourthCell
              index={index}
              type={type}
              styles={{ justifySelf: "flex-end" }}
              classFromParent={"cart-page-mobile-type"}
            />
            <LineItem>Quantity</LineItem>
            <ThirdCell
              styles={{ justifySelf: "flex-end" }}
              card={card}
              clearTheMessage={clearCartBuyErrorMessage}
              confirmDelete={confirmDelete}
              clearConfirmModal={clearConfirmModal}
              cartError={cartError}
              cartOpenConfirmModal={cartOpenConfirmModal}
              cartLoading={cartLoading}
              loadingAction={CART_LOADING}
              updateCartError={UPDATE_CART_BUY_ERROR}
              classFromParent={"cart-page-mobile-quantity"}
            />
          </MobileCardGrid>
        </MobileCard>
      </MainMobileContainer>
    </CardContainer>
  );
};
export default CardInBigBuyCart;
